import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInquiry } from '../../contexts/InquiryContext';
import ArtworkModal from './ArtworkModal';

interface ArtworkCardProps {
    id: string;
    title: string;
    imageUrl: string;
    images?: string[];
    price: number;
    description?: string;
    year?: number;
    type?: string;
    status?: string;
}

const ArtworkCard: React.FC<ArtworkCardProps> = ({ id, title, imageUrl, images = [], price, description, year, type, status }) => {
    const { state, addItem, removeItem } = useInquiry();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const isSelected = state.items.some(item => item.id === id);

    // Combine main imageUrl with images array if not already present
    const allImages = images.length > 0 ? images : [imageUrl];

    const toggleInquiry = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (isSelected) {
            removeItem(id);
        } else {
            addItem({ id, title, imageUrl, type });
        }
    };

    const openSlideshow = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsModalOpen(true);
    };

    return (
        <>
            <article className={`card-luxury group flex flex-col h-full fade-in-up transition-all duration-700 ${isSelected ? 'ring-1 ring-accent bg-gray-50/30' : ''}`} aria-labelledby={`title-${id}`}>
                <div
                    onClick={openSlideshow}
                    className="block aspect-[4/3] overflow-hidden bg-gray-100 relative focus:outline-none cursor-pointer"
                    aria-label={`Bekijk slideshow van kunstwerk: ${title}`}
                >
                    <img
                        src={imageUrl}
                        alt={`Kunstwerk van Clemens Briels getiteld ${title}. ${type || ''} uit ${year || ''}.`}
                        className="w-full h-full object-contain transition-transform duration-1000 group-hover:scale-110"
                        loading="lazy"
                        onError={(e: any) => {
                            e.target.src = 'https://placehold.co/800x600/f3f4f6/a1a1aa?text=Beeld+niet+beschikbaar';
                        }}
                    />

                    {/* Selection Overlay Indicator */}
                    <div
                        onClick={toggleInquiry}
                        className={`absolute top-6 right-6 w-8 h-8 rounded-full border border-accent/20 flex items-center justify-center transition-all duration-500 z-20 ${isSelected ? 'bg-accent border-accent text-white scale-110 shadow-lg' : 'bg-white/80 backdrop-blur-sm group-hover:scale-110'}`}
                    >
                        {isSelected ? '✓' : '+'}
                    </div>

                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500 flex items-center justify-center" aria-hidden="true">
                        <span className="opacity-0 group-hover:opacity-100 bg-black/40 text-white px-4 py-2 text-[10px] uppercase tracking-widest backdrop-blur-sm transition-opacity duration-300">
                            Bekijk foto's
                        </span>
                    </div>

                    {status === 'sold' && (
                        <div className="absolute top-4 left-4 bg-gray-900 text-white text-[10px] uppercase tracking-widest px-4 py-2 font-bold shadow-lg z-10">
                            Verkocht
                        </div>
                    )}
                </div>

                <div className="p-8 flex flex-col flex-grow bg-white">
                    <div className="flex justify-between items-baseline mb-4">
                        <span className="text-[10px] font-bold tracking-[0.2em] text-accent uppercase">{type || 'Exclusieve Collectie'}</span>
                        <span className="text-xs text-gray-400 font-serif italic">{year || '2004'}</span>
                    </div>

                    <h3 id={`title-${id}`} className="text-2xl font-serif text-primary mb-4 group-hover:text-accent transition-colors duration-300 leading-snug">
                        <button onClick={openSlideshow} className="text-left focus:outline-none">{title}</button>
                    </h3>

                    {description && (
                        <p className="text-gray-500 text-sm leading-relaxed mb-8 line-clamp-2 font-light">
                            {description}
                        </p>
                    )}

                    <div className="mt-auto pt-6 border-t border-gray-50">
                        <div className="flex justify-between items-center mb-6">
                            <div className="flex flex-col">
                                <span className="text-[10px] uppercase tracking-widest text-gray-400 mb-1 font-bold">Investering</span>
                                <span className="text-lg font-serif italic text-primary">Prijs op aanvraag</span>
                            </div>
                            <Link
                                to={`/artwork/${id}`}
                                className="text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-accent transition-colors border-b border-gray-100 hover:border-accent pb-1"
                            >
                                Bekijk details
                            </Link>
                        </div>

                        <button
                            onClick={toggleInquiry}
                            disabled={status === 'sold'}
                            className={`w-full py-4 uppercase text-[10px] tracking-[0.3em] font-bold transition-all duration-500 ${
                                status === 'sold'
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : isSelected
                                        ? 'bg-accent text-white shadow-accent/20'
                                        : 'bg-primary text-white hover:bg-accent shadow-lg hover:shadow-accent/20'
                            }`}
                        >
                            {status === 'sold' ? 'Niet meer beschikbaar' : isSelected ? 'In aanvraaglijst' : 'Selecteer voor aanvraag'}
                        </button>
                    </div>
                </div>
            </article>

            <ArtworkModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                images={allImages}
                title={title}
            />
        </>
    );
};

export default ArtworkCard;